import { useEffect, useRef, useState } from 'react'
import {
  isOrderFeedSource,
  usePotentials,
  useRemovePotential,
  useVoucherCap,
} from '../basket/potentials'
import { useTranslation } from '../localization/useTranslation'
import { useAuthStore } from '../store/auth'
import { useSelectionStore } from '../store/selection'
import { GlyphBag, GlyphShirt, IconArrowRight, IconX } from '../ui/icons'
import { useMoneyFmt } from '../util/useMoney'

interface BasketSummaryProps {
  ctaLabel: string
  ctaShowArrow: boolean
  continueDisabled: boolean
  showBack: boolean
  onContinue: () => void
  onBack: () => void
}

/**
 * Persistent "Your trade-in" rail (mockup `.rail`), backed by live Potentials from Core. Shows
 * the added items, the running reward (serif, gold, pulses on increase) and owns the Back /
 * Continue nav for the flow. Removing an item happens here — the single, consistent control.
 */
export function BasketSummary({
  ctaLabel,
  ctaShowArrow,
  continueDisabled,
  showBack,
  onContinue,
  onBack,
}: BasketSummaryProps) {
  const { potentials, reward, loading } = usePotentials()
  const { max, exceeded } = useVoucherCap()
  const { remove, removing } = useRemovePotential()
  const deselect = useSelectionStore((s) => s.deselect)
  const defaultImage = useAuthStore((s) => s.config.defaultImage)
  const { t } = useTranslation()

  // When both past-order items and manual items share the basket, label the manual ones.
  const hasFeedItem = potentials.some((p) => isOrderFeedSource(p.sourceType))

  // Pulse the reward figure whenever the total grows (an item was added).
  const [pulse, setPulse] = useState(false)
  const prevReward = useRef(reward)
  useEffect(() => {
    if (reward > prevReward.current) {
      setPulse(true)
      const t = setTimeout(() => setPulse(false), 400)
      prevReward.current = reward
      return () => clearTimeout(t)
    }
    prevReward.current = reward
  }, [reward])

  const n = potentials.length
  const fmt = useMoneyFmt()

  return (
    <div className="rail">
      <div className="rail-head">
        <h3>{t('rail.title')}</h3>
        <span className="count">
          {n === 1 ? t('rail.itemCount', { count: n }) : t('rail.itemCountPlural', { count: n })}
        </span>
      </div>

      {n === 0 ? (
        <div className="rail-empty">
          <span className="rr">
            <GlyphBag />
          </span>
          {loading ? t('rail.loading') : t('rail.empty')}
        </div>
      ) : (
        <div className="rail-body">
          {potentials.map((p) => {
            const image = p.imageUrls?.[0] || defaultImage
            const isFeed = isOrderFeedSource(p.sourceType)
            return (
              <div className="rail-item" key={p._id}>
                <span className="thumb" style={{ width: 38, height: 38 }}>
                  {image ? <img src={image} alt="" /> : <GlyphShirt />}
                </span>
                <span className="meta">
                  <div className="nm">
                    {p.name}
                  </div>
                  {hasFeedItem && !isFeed && (
                    <div className="tagline">{t('rail.addedManually')}</div>
                  )}
                </span>
                <span className="amt num">
                  {fmt(p.offer?.value)}
                </span>
                <button
                  type="button"
                  className="icon-btn rm-rail"
                  aria-label={t('rail.remove', { name: p.name ?? '' })}
                  disabled={!isFeed && removing}
                  // Feed items are deselected (the past-order potential stays on Core); manual
                  // items are deleted.
                  onClick={() => (isFeed ? deselect(p._id) : remove(p._id))}
                >
                  <IconX />
                </button>
              </div>
            )
          })}
        </div>
      )}

      <div className="rail-foot">
        <div className="reward-block">
          <div className="reward-row">
            <span className="k">{t('rail.estimatedReward')}</span>
          </div>
          <div className={`reward-big num ${pulse ? 'pulse' : ''}`}>
            {fmt(reward)}
          </div>
          <div className="reward-note">
            {n > 0 ? t('rail.rewardNoteConfirmed') : t('rail.rewardNoteAdd')}
          </div>
        </div>
        {exceeded && <div className="rail-warn">{t('rail.maxExceeded', { amount: fmt(max) })}</div>}
        <button
          type="button"
          className="btn btn-primary"
          disabled={continueDisabled}
          onClick={onContinue}
        >
          {ctaLabel}
          {ctaShowArrow && <IconArrowRight />}
        </button>
      </div>

      {showBack && (
        <button type="button" className="rail-back" onClick={onBack}>
          ← {t('rail.back')}
        </button>
      )}
    </div>
  )
}
